import { DropzoneField } from '@lfg/ui/components';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import { Box, Grid2, Typography } from '@mui/material';

import { colors } from 'src/global/styles/colors';
import { useTheme } from 'src/provider/ThemeCustomizationProvider';

type ImageDropzoneProps = {
  label: string;
  height: string;
  file: File | undefined;
  onDrop: (files: File[]) => void;
  onClear: () => void;
  gridItemProps?: any;
  gridSize?: number;
  isError?: boolean;
  errorMsg?: string;
  disabled?: boolean;
};

const ImageDropzone: React.FC<ImageDropzoneProps> = ({
  label,
  height,
  file,
  onDrop,
  gridSize,
  onClear,
  isError,
  errorMsg,
  disabled,
}) => (
  <Grid2 size={gridSize || 4}>
    <DropzoneField
      maxFiles={1}
      containerSx={{
        border: `1px solid ${isError ? colors.error : colors.neutral015}`,
        borderRadius: '4px',
        height,
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      iconWrapperStyle={{
        top: 0,
        right: 0,
      }}
      disabled={disabled}
      onDrop={onDrop}
      files={file ? [file] : []}
      onClear={onClear}
      emptyContent={<ImageSearchIcon sx={{ width: '24px', height: '24px', color: 'white' }} />}
    />
    {isError ? (
      <Typography paddingTop={1} color={colors.error} fontSize={'14px'}>
        {errorMsg}
      </Typography>
    ) : null}
    <Typography
      textAlign={'center'}
      marginTop={2}
      fontSize={'14px'}
      fontWeight={400}
      lineHeight={'20px'}
      sx={{ color: 'white' }}>
      {label}
    </Typography>
  </Grid2>
);

export function ImageSection() {
  const { theme, updateTheme, themeError, setThemeError } = useTheme();

  const handleDrop = (key: keyof typeof theme.image) => (files: File[], rejectFiles?: any[]) => {
    if (themeError[key].isError) {
      setThemeError?.({
        ...themeError,
        [key]: { isError: false, errorMsg: '' },
      });
    }

    updateTheme({
      ...theme,
      image: {
        ...theme.image,
        [key]: files[0],
      },
    });
    if (rejectFiles && rejectFiles.length > 0) {
      setThemeError?.({
        ...themeError,
        [key]: { isError: true, errorMsg: 'Maximum file size is 10MB' },
      });
    }
  };

  return (
    <Box width={'100%'} padding={3}>
      <Typography marginBottom={2} fontSize={'16px'} fontWeight={500} lineHeight={'20px'} sx={{ color: 'white' }}>
        Images
      </Typography>
      <Grid2 container spacing={2}>
        <ImageDropzone
          gridSize={6}
          label='Logo'
          height='114px'
          file={theme.image.logo}
          isError={themeError?.logo?.isError}
          errorMsg={themeError?.logo?.errorMsg}
          onDrop={handleDrop('logo')}
          onClear={() => updateTheme({ ...theme, image: { ...theme.image, logo: undefined } })}
        />
        <ImageDropzone
          gridSize={6}
          label='Favicon'
          height='114px'
          file={theme.image.favicon}
          isError={themeError?.favicon?.isError}
          errorMsg={themeError?.favicon?.errorMsg}
          onDrop={handleDrop('favicon')}
          onClear={() => updateTheme({ ...theme, image: { ...theme.image, favicon: undefined } })}
        />
        <ImageDropzone
          gridSize={12}
          label='Featured Image'
          height='200px'
          file={theme.image.featureImage}
          isError={themeError?.featureImage?.isError}
          errorMsg={themeError?.featureImage?.errorMsg}
          onDrop={handleDrop('featureImage')}
          onClear={() => updateTheme({ ...theme, image: { ...theme.image, featureImage: undefined } })}
        />
      </Grid2>
      <ul className='ml-[-28px] flex gap-x-6 items-center text-[12px] text-[#9C9BA3]'>
        <li>Supported formats: jpeg, png, gif</li>
        <li>Maximum file size: 10MB </li>
      </ul>
    </Box>
  );
}
